import { EMPLOYEE_ROLE_LABELS, NO_ROLE_LABEL } from "@etudescarre/constants";
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  Modal,
  Select,
  Tabs,
} from "@etudescarre/ui";
import {
  ArrowClockwiseIcon,
  LockKeyIcon,
  PencilIcon,
  PlusIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { useCallback, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import {
  AdminListPagination,
  AdminListSearch,
  useAdminListQuery,
} from "./components";
import { PendingInvitesList } from "./pending-invites-list";
import { UserEditForm } from "./user-edit-form";
import { UserInviteForm } from "./user-invite-form";

type User = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  role: "root" | "admin" | "user" | null;
  emailVerified: boolean;
  isActive: boolean;
  createdAt: string;
  company: {
    id: number;
    name: string;
  };
};

// "none" = contact-only, no role assigned.
const NO_ROLE_VALUE = "none";

const roleColors: Record<
  "root" | "admin" | "user",
  "purple" | "blue" | "default"
> = {
  root: "purple",
  admin: "blue",
  user: "default",
};

export function UsersList() {
  const [inviteDrawerOpen, setInviteDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [filterRole, setFilterRole] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState("users");
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [userToReset, setUserToReset] = useState<User | null>(null);
  const [isSendingReset, setIsSendingReset] = useState(false);

  const fetcher = useCallback(
    async ({
      search,
      page,
      pageSize,
    }: {
      search?: string;
      page: number;
      pageSize: number;
    }) => {
      const { data, error } = await apiClient.GET("/api/admin/users", {
        params: {
          query: {
            search: search ?? undefined,
            page,
            pageSize,
          },
        },
      });
      if (error) {
        throw new Error("Erreur lors du chargement des utilisateurs");
      }
      return {
        items: data.data.users,
        total: data.data.total,
      };
    },
    [],
  );

  const {
    items: allUsers,
    total,
    loading,
    error,
    search,
    page,
    pageSize,
    setSearch,
    setPage,
    setPageSize,
    refetch,
  } = useAdminListQuery<User>(fetcher, "users");

  const filteredUsers = allUsers.filter((user) => {
    if (
      filterRole.length > 0 &&
      !filterRole.includes(user.role ?? NO_ROLE_VALUE)
    ) {
      return false;
    }
    if (filterStatus.length > 0) {
      const statusValue = user.emailVerified ? "verified" : "pending";
      if (!filterStatus.includes(statusValue)) {
        return false;
      }
    }
    return true;
  });

  const handleInvite = () => {
    setInviteDrawerOpen(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditDrawerOpen(true);
  };

  const handlePasswordResetClick = (user: User) => {
    setUserToReset(user);
    setPasswordResetModalOpen(true);
  };

  const handlePasswordResetConfirm = async () => {
    if (!userToReset) return;

    setIsSendingReset(true);
    const { error: resetError } = await apiClient.POST(
      "/api/admin/users/{userId}/password-reset",
      {
        params: { path: { userId: userToReset.id } },
      },
    );

    if (resetError) {
      toaster.error({
        title: "Erreur",
        description: "Échec de l'envoi de l'email de réinitialisation",
      });
    } else {
      toaster.success({
        title: "Succès",
        description: `Email de réinitialisation envoyé à ${userToReset.email}`,
      });
    }

    setIsSendingReset(false);
    setPasswordResetModalOpen(false);
    setUserToReset(null);
  };

  const handleFormSuccess = () => {
    refetch();
  };

  const roleOptions = [
    { label: EMPLOYEE_ROLE_LABELS.root, value: "root" },
    { label: EMPLOYEE_ROLE_LABELS.admin, value: "admin" },
    { label: EMPLOYEE_ROLE_LABELS.user, value: "user" },
    { label: `${NO_ROLE_LABEL} (contact)`, value: NO_ROLE_VALUE },
  ];

  const statusOptions = [
    { label: "Actif", value: "verified" },
    { label: "En attente", value: "pending" },
  ];

  const columns: DataTableColumn<User>[] = [
    {
      id: "name",
      header: "Nom",
      accessor: (row) =>
        [row.firstName, row.lastName].filter(Boolean).join(" ") || "-",
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "email",
      header: "Email",
      accessor: (row) => row.email ?? "—",
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "role",
      header: "Rôle",
      accessor: (row) =>
        row.role ? EMPLOYEE_ROLE_LABELS[row.role] : NO_ROLE_LABEL,
      cell: ({ row }) =>
        row.role ? (
          <Badge variant={roleColors[row.role]}>
            {EMPLOYEE_ROLE_LABELS[row.role]}
          </Badge>
        ) : (
          <Badge variant="default">{NO_ROLE_LABEL}</Badge>
        ),
      loadingCell: "badge",
    },
    {
      id: "emailVerified",
      header: "Statut",
      accessor: (row) => (row.emailVerified ? "Actif" : "En attente"),
      cell: ({ row }) => (
        <Badge variant={row.emailVerified ? "green" : "yellow"}>
          {row.emailVerified ? "Actif" : "En attente"}
        </Badge>
      ),
      loadingCell: "badge",
    },
    {
      id: "createdAt",
      header: "Créé le",
      accessor: (row) => new Date(row.createdAt).toLocaleDateString("fr-FR"),
      loadingCell: "text",
    },
    {
      id: "actions",
      header: "Actions",
      accessor: () => "",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button plain onClick={() => handleEdit(row)} aria-label="Modifier">
            <PencilIcon className="size-4" />
          </Button>
          {row.email && (
            <Button
              plain
              onClick={() => handlePasswordResetClick(row)}
              aria-label="Réinitialiser le mot de passe"
              title="Réinitialiser le mot de passe"
            >
              <LockKeyIcon className="size-4" />
            </Button>
          )}
          {!row.emailVerified && row.email && (
            <Button
              plain
              onClick={async () => {
                const { error: resendError } = await apiClient.POST(
                  "/api/admin/users/{userId}/resend-invite",
                  { params: { path: { userId: row.id } } },
                );
                if (resendError) {
                  toaster.error({
                    title: "Erreur",
                    description: "Échec du renvoi de l'invitation",
                  });
                } else {
                  toaster.success({
                    title: "Succès",
                    description: `Invitation renvoyée à ${row.email}`,
                  });
                }
              }}
              aria-label="Renvoyer l'invitation"
              title="Renvoyer l'invitation"
            >
              <ArrowClockwiseIcon className="size-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Utilisateurs
          </h2>
          <Button onClick={handleInvite}>
            <PlusIcon data-slot="icon" className="size-4" />
            Inviter
          </Button>
        </div>

        <Tabs.Root
          value={activeTab}
          onValueChange={(details) => setActiveTab(details.value)}
        >
          <Tabs.List className="mb-4">
            <Tabs.Trigger value="users">Utilisateurs actifs</Tabs.Trigger>
            <Tabs.Trigger value="invites">Invitations en attente</Tabs.Trigger>
            <Tabs.Indicator />
          </Tabs.List>

          <Tabs.Content value="users">
            <div className="mb-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <AdminListSearch
                  value={search}
                  onChange={setSearch}
                  placeholder="Rechercher par nom ou email..."
                  label="Recherche"
                  id="users-search"
                />
                <Select
                  options={roleOptions}
                  value={filterRole}
                  onValueChange={({ value }) => setFilterRole(value)}
                  placeholder="Tous les rôles"
                  label="Rôle"
                />
                <Select
                  options={statusOptions}
                  value={filterStatus}
                  onValueChange={({ value }) => setFilterStatus(value)}
                  placeholder="Tous les statuts"
                  label="Statut"
                />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredUsers}
              isLoading={loading}
              isEmpty={filteredUsers.length === 0}
              emptyState={
                <div className="flex min-h-[200px] items-center justify-center text-gray-500">
                  {search
                    ? "Aucun résultat pour cette recherche"
                    : "Aucune donnée"}
                </div>
              }
            />

            <AdminListPagination
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </Tabs.Content>

          <Tabs.Content value="invites">
            <PendingInvitesList onInviteRevoked={refetch} />
          </Tabs.Content>
        </Tabs.Root>
      </div>

      <UserInviteForm
        open={inviteDrawerOpen}
        onOpenChange={setInviteDrawerOpen}
        onSuccess={handleFormSuccess}
      />

      {editingUser && (
        <UserEditForm
          open={editDrawerOpen}
          onOpenChange={setEditDrawerOpen}
          user={editingUser}
          onSuccess={handleFormSuccess}
        />
      )}

      <Modal.Root
        open={passwordResetModalOpen}
        onOpenChange={(details) => {
          if (!isSendingReset) {
            setPasswordResetModalOpen(details.open);
          }
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content size="sm">
              <Modal.Header>
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                    <WarningIcon className="size-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <Modal.Title>Réinitialiser le mot de passe</Modal.Title>
                </div>
              </Modal.Header>
              <Modal.Body>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  Un email de réinitialisation de mot de passe sera envoyé à{" "}
                  <strong>{userToReset?.email}</strong>. L'utilisateur devra
                  cliquer sur le lien dans l'email pour définir un nouveau mot
                  de passe.
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setPasswordResetModalOpen(false)}
                  disabled={isSendingReset}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handlePasswordResetConfirm}
                  disabled={isSendingReset}
                >
                  {isSendingReset ? "En cours..." : "Envoyer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}
